import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";

const apiBase = import.meta.env.VITE_API_BASE;

export default function InviteUser() {
    const { role } = useAuth();
    const [email, setEmail] = useState("");
    const [roleToInvite, setRoleToInvite] = useState("ExamAdmin");
    const [organizationId, setOrganizationId] = useState<number | null>(null);
    const [organizations, setOrganizations] = useState<
        { id: number; name: string }[]
    >([]);
    const [message, setMessage] = useState("");

    // ✅ Load organizations
    useEffect(() => {
        async function loadOrgs() {
            try {
                const token =
                    localStorage.getItem("authToken") ||
                    sessionStorage.getItem("authToken");

                const res = await fetch(`${apiBase}/api/organizations`, {
                    headers: { Authorization: `Bearer ${token}` },
                });

                if (!res.ok) throw new Error(await res.text());

                const data = await res.json();
                setOrganizations(data);
            } catch (err: any) {
                console.error("Failed to load organizations:", err.message);
                setMessage("❌ Failed to load organizations.");
            }
        }
        if (role === "SuperAdmin") {
            loadOrgs();
        }
    }, [role]);

    // ✅ Handle invite
    async function handleInvite(e: React.FormEvent) {
        e.preventDefault();
        setMessage("");

        try {
            const token =
                localStorage.getItem("authToken") ||
                sessionStorage.getItem("authToken");

            const res = await fetch(`${apiBase}/api/user/invite`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    email,
                    role: roleToInvite,
                    organizationId,
                }),
            });

            if (!res.ok) throw new Error(await res.text());

            setMessage(`✅ Invite sent to ${email}`);
            setEmail("");
        } catch (err: any) {
            console.error("Error sending invite:", err.message);
            setMessage(`❌ ${err.message}`);
        }
    }

    if (role !== "SuperAdmin") {
        return <p className="p-6">❌ Access denied.</p>;
    }

    return (
        <div className="p-6 max-w-lg mx-auto bg-white rounded shadow">
            <h1 className="text-2xl font-bold mb-6">Invite New User</h1>

            <form onSubmit={handleInvite} className="space-y-4">
                <div>
                    <label className="block text-sm font-medium">Email</label>
                    <input
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full border rounded px-3 py-2"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium">Role</label>
                    <select
                        value={roleToInvite}
                        onChange={(e) => setRoleToInvite(e.target.value)}
                        className="w-full border rounded px-3 py-2"
                    >
                        <option value="ExamAdmin">ExamAdmin</option>
                        <option value="Candidate">Candidate</option>
                    </select>
                </div>

                <div>
                    <label className="block text-sm font-medium">Organization</label>
                    <select
                        required
                        value={organizationId ?? ""}
                        onChange={(e) => setOrganizationId(Number(e.target.value))}
                        className="w-full border rounded px-3 py-2"
                    >
                        <option value="">-- Select Organization --</option>
                        {organizations.map((o) => (
                            <option key={o.id} value={o.id}>
                                {o.name}
                            </option>
                        ))}
                    </select>
                </div>

                <button
                    type="submit"
                    className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700"
                >
                    Send Invite
                </button>
            </form>

            {message && <p className="mt-4 text-sm">{message}</p>}
        </div>
    );
}
